import React, { useEffect } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import { Alert, TaskCard, TextField } from '@zealy/design-system';
import { getTweetIdFromUrl, isTweetUrlValid } from '@zealy/utils';

import { useError } from '#app/cw/[subdomain]/(app)/questboard/admin/[questId]/_components/FormError.context';
import { Tweet as TweetCard } from '#components/Tweet';

import type { ClaimTaskInput, QuestTask } from '../Tasks.types';
import { withQuest } from '../Tasks.funcs';

type WrapperProps = Extract<QuestTask, { type: 'tweetReact' }>;

export const TweetReactTask = ({ task, error }: { task: WrapperProps; error?: string }) => {
  const t = useTranslations();
  const { register } = useFormContext<Record<string, ClaimTaskInput<'tweetReact'>>>();

  const tweetId = getTweetIdFromUrl(task.settings.tweetUrl);

  return (
    <TaskCard type="twitter" title={t('ds.social.twitter.reactions.header')}>
      <div className="flex flex-col gap-300 px-200 py-400">
        <TweetCard
          input={{
            tweetId: tweetId,
          }}
          actions={task.settings.actions}
          tweetCreator={task.metadata?.creator}
          tweetText={task.metadata?.content}
          createdAt={task.metadata?.createdAt}
        />
        {task.settings.engagementMetrics && task.settings.actions?.includes('reply') && (
          <Alert
            title={t('quest.type.tweetReact.engagementMetrics.requirement')}
            description={
              <div className="flex flex-col gap-100">
                {task.settings.engagementMetrics.minLikes !== undefined && (
                  <p className="min-w-0">
                    • At least{' '}
                    <span className="font-semibold">
                      {task.settings.engagementMetrics.minLikes}
                    </span>{' '}
                    {task.settings.engagementMetrics.minLikes === 1 ? 'like' : 'likes'}
                  </p>
                )}
                {task.settings.engagementMetrics.minImpressions !== undefined && (
                  <p className="min-w-0">
                    • At least{' '}
                    <span className="font-semibold">
                      {task.settings.engagementMetrics.minImpressions}
                    </span>{' '}
                    {task.settings.engagementMetrics.minImpressions === 1
                      ? 'impression'
                      : 'impressions'}
                  </p>
                )}
                {task.settings.engagementMetrics.minReplies !== undefined && (
                  <p className="min-w-0">
                    • At least{' '}
                    <span className="font-semibold">
                      {task.settings.engagementMetrics.minReplies}
                    </span>{' '}
                    {task.settings.engagementMetrics.minReplies === 1 ? 'reply' : 'replies'}
                  </p>
                )}
                {task.settings.engagementMetrics.minRetweets !== undefined && (
                  <p className="min-w-0">
                    • At least{' '}
                    <span className="font-semibold">
                      {task.settings.engagementMetrics.minRetweets}
                    </span>{' '}
                    {task.settings.engagementMetrics.minRetweets === 1 ? 'retweet' : 'retweets'}
                  </p>
                )}
              </div>
            }
          />
        )}
        {task.settings.actions?.includes('reply') && (
          <TextField
            label={t('quest.type.tweetReact.fields.replyUrl.label')}
            placeholder={t('quest.type.tweetReact.fields.replyUrl.placeholder')}
            isInvalid={!!error}
            hint={error}
            {...register(`${task.id}.tweetUrl`)}
          />
        )}
      </div>
    </TaskCard>
  );
};

export const TweetReactClaim = withQuest<unknown, 'tweetReact'>(({ task }) => {
  const t = useTranslations();

  const { formState, watch } = useFormContext<Record<string, ClaimTaskInput<'tweetReact'>>>();

  const { fieldState } = useController({
    name: task.id,
    defaultValue: {
      taskId: task.id,
      reply: undefined,
      type: 'tweetReact' as const,
      tweetUrl: '',
    },
  });

  const { clearError, error, setError } = useError(
    `${task.id}.tweetUrl`,
    !task.settings.actions?.includes('reply') ? undefined : 'Required',
  );

  const tweetUrl = watch(`${task.id}.tweetUrl`);

  useEffect(() => {
    if (!task.settings.actions?.includes('reply')) return;

    const isValidTweetURL = !!tweetUrl && isTweetUrlValid(tweetUrl);
    if (isValidTweetURL) {
      clearError();
    } else if (!isValidTweetURL) {
      setError('Invalid tweet URL');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tweetUrl]);

  return (
    <TweetReactTask
      task={task}
      error={
        fieldState.isDirty
          ? error ??
            formState.errors?.[task.id]?.message ??
            formState.errors?.[task.id]?.tweetUrl?.message
          : t('quest.type.tweetReact.fields.replyUrl.hint')
      }
    />
  );
});
